import {useAuthStore} from "@/stores";

/**
 * Gửi 1 request POST và đọc phản hồi SSE dạng STREAM (dùng fetch — axios trên
 * browser không đọc được response body dạng stream).
 * `onDelta` được gọi mỗi khi có thêm 1 đoạn text; Promise resolve khi stream kết thúc.
 *
 * Dùng chung cho meeting chat (`/task/chat/send_stream`) và project chat
 * (`/projects/chat/send_stream`) — cùng định dạng SSE của engine.
 */
export async function streamChatSSE(
    path: string,
    body: Record<string, unknown>,
    onDelta: (delta: string) => void,
    signal?: AbortSignal
): Promise<void> {
    const {accessToken} = useAuthStore();
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
    };
    if (accessToken) headers["Authorization"] = `${accessToken}`;
    const shareToken = sessionStorage.getItem("share_token");
    if (shareToken) headers["X-Share-Token"] = shareToken;

    const res = await fetch(`${process.env.VUE_APP_API_WRAP_URL}${path}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal,
    });

    if (!res.ok || !res.body) {
        throw new Error(`Stream request failed (${res.status})`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    // Tách 1 payload SSE thành đoạn text hiển thị.
    // Tolerant: hỗ trợ raw text hoặc JSON với nhiều key phổ biến.
    const extractDelta = (payload: string): string => {
        const trimmed = payload.trim();
        if (!trimmed) return "";
        try {
            const obj = JSON.parse(trimmed);
            if (typeof obj === "string") return obj;
            return (
                obj?.data?.content ?? // format engine: {"data":{"content":"..."}}
                obj.content ??
                obj.delta ??
                obj.text ??
                obj.token ??
                obj?.choices?.[0]?.delta?.content ??
                ""
            );
        } catch {
            return payload;
        }
    };

    // Xử lý 1 block SSE (các dòng ngăn bởi \n, phân tách event bởi \n\n)
    const handleEvent = (rawEvent: string): boolean => {
        const dataLines = rawEvent
            .split(/\r?\n/)
            .filter((l) => l.startsWith("data:"))
            .map((l) => l.slice(5).replace(/^ /, ""));
        if (dataLines.length === 0) return false;
        const payload = dataLines.join("\n");
        if (payload.trim() === "[DONE]") return true; // sentinel kết thúc
        const delta = extractDelta(payload);
        if (delta) onDelta(delta);
        return false;
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const {value, done} = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, {stream: true});

        // Event SSE phân tách bởi dòng trống (\n\n hoặc \r\n\r\n)
        let sep = /\r?\n\r?\n/.exec(buffer);
        while (sep) {
            const rawEvent = buffer.slice(0, sep.index);
            buffer = buffer.slice(sep.index + sep[0].length);
            const isDone = handleEvent(rawEvent);
            if (isDone) return;
            sep = /\r?\n\r?\n/.exec(buffer);
        }
    }

    // Flush phần còn sót lại (nếu server không kết thúc bằng dòng trống)
    if (buffer.trim()) handleEvent(buffer);
}
